import React, { useEffect, useState, useRef, useCallback } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import {
  reopenExtendable,
  setExtendableType,
  setExtendableId
} from "../../store/actions/edit";
import LoadingCircle from "../common/LoadingCircle";
import {
  clearDriverLocations,
  reoptimizeRoutes,
  setCurrentWindow,
  setSelectedRoutes
} from "../../store/actions/project";

import { Box, makeStyles, Slide, Grid } from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import SettingsIcon from "@material-ui/icons/Settings";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import PinIcon from "@material-ui/icons/Room";
import TruckIcon from "@material-ui/icons/LocalShipping";
import Button from "@material-ui/core/Button";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import UnassignedIcon from "@material-ui/icons/EventBusy";
import AddTasksIcon from "@material-ui/icons/EventAvailable";
import DriversIcon from "@material-ui/icons/People";
import EventBusyIcon from "@material-ui/icons/EventBusy";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";

import Fade from "@material-ui/core/Fade";
import {
  remCalc,
  isEmptyObj,
  countStops,
  capitalize
} from "../../utils/helpers";
import Badge from "@material-ui/core/Badge";
import StopStats from "../project/StopStats";
import {
  setProjectTabValue,
  setOptionsExpanded,
  setDragSelection
} from "../../store/actions/common";
import RouteGroups from "./RouteGroups";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";
import OptimizedTooltip from "../common/OptimizedTooltip";
import RoutesToolbar from "./RoutesToolbar";
import Route from "./Route";
import FormDialog from "../dialogs/FormDialog";
import { emailRegex } from "../../utils/regExps";
import API from "../../utils/axios";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";
import { useHistory } from "react-router-dom";
import { withSnackbar } from "notistack";
import AddDriverDialog from "./AddDriverDialog";
import ConfirmDialog from "../dialogs/ConfirmDialog";

const useStyles = makeStyles(theme => ({
  scheduled: {
    fontSize: "0.75rem",
    fontWeight: 500
  },
  header: {
    minHeight: 72,
    borderTop: theme.variables.border,
    borderBottom: theme.variables.border,
    backgroundColor: "#fff"
  },
  iconUnshceduled: {
    marginRight: theme.spacing(1.5)
  },
  expandIcon: {
    transition: "transform .3s"
  },
  rotate180: {
    transform: "rotate(180deg)"
  },
  routesList: {
    maxHeight: "calc(100vh - 80px)",
    height: "calc(100vh - 80px)",
    overflowY: "auto",
    position: "relative",
    backfaceVisibility: "hidden",
    boxSizing: "border-box"
  },
  routesListSm: {
    paddingBottom: 60
  },
  routeWrapper: {
    maxHeight: "calc(100vh - 80px)",
    height: "calc(100vh - 80px)"
  },
  tabButton: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: remCalc(15),
    textTransform: "none",
    padding: theme.spacing(0.75, 2, 0.75, 1.5),
    borderColor: "transparent",
    marginRight: 8,
    color: "#5F6368",

    "&:hover": {
      borderColor: "#e3e9f9"
    }
  },
  verticalDivider: {
    height: 24,
    alignSelf: "center",
    borderRight: "1px solid #DDDDDD",
    width: 1
  }
}));

function Optimized({
  published,
  stopsLength,
  vehiclesLength,
  currentWindow,
  loadingRoutesList,
  setExtendableType,
  setExtendableId,
  reopenExtendable,
  setProjectTabValue,
  setCurrentWindow,
  clearDriverLocations,
  saveDisabled,
  settingsSaveDisabled,
  plan,
  solution,
  optionsExpanded,
  setOptionsExpanded,
  reoptimizeRoutes,
  showingRouteId,
  enqueueSnackbar,
  projectId,
  userData,
  unassignedStopsCount,
  isEditMode,
  isSelectedRoutesHasStartedRoutes,
  selectedRoutes,
  setSelectedRoutes,
  projectTabValue,
  setDragSelection
}) {
  const classes = useStyles();
  const history = useHistory();

  //States
  const [statsExpanded, setStatsExpanded] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [shareRouteIds, setShareRouteIds] = useState([]);
  const [shareRouteEmail, setShareRouteEmail] = useState("");
  const [shareRouteEmailError, setShareRouteEmailError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({});
  const [changesDialog, setChangesDialog] = useState({ visible: false });
  const [isSharingRoute, setIsSharingRoute] = useState(false);

  const productTour = useProductTour();
  const isProductTOurStepProjectPublishShareDialog = productTour.isTourStepId(
    PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_SHARE_DIALOG
  );

  //Effects
  useEffect(() => {
    return () => {
      setSelectedRoutes([]);
      setDragSelection({});
    };
  }, [projectTabValue]);

  // Share route
  const shareRoute = useCallback(ids => {
    setShareRouteIds(ids);
    setShowShare(true);
  }, []);

  const handleShareClose = () => {
    setShareRouteIds([]);
    setShareRouteEmailError("");
    setShowShare(false);
  };

  // Send an email
  const handleSend = e => {
    e.preventDefault();
    setIsSharingRoute(true);
    if (shareRouteEmail.trim() === "") {
      setShareRouteEmailError("Email is required.");
      setIsSharingRoute(false);
      return;
    }
    if (!emailRegex.test(shareRouteEmail.trim())) {
      setShareRouteEmailError("Email is invalid.");
      setIsSharingRoute(false);
      return;
    }

    setShareRouteEmailError("");
    // Prevent dublicate email sending case when user clicks on button rapid multiple times
    if (isSharingRoute) {
      return;
    }

    const sendEmailRequests = shareRouteIds.map(async routeId => {
      return await API.post(
        `/v1/projects/${projectId}/routes/${routeId}/send/email`,
        {
          shareRouteEmail
        }
      );
    });
    Promise.all(sendEmailRequests)
      .then(res => {
        // Prevent multiple success snackbar when user is already fast clicked and clicking to send email for drivers
        res.length > 0 && enqueueSnackbar("Email successfully sent");

        handleShareClose();
        if (isProductTOurStepProjectPublishShareDialog) {
          history.push("/projects");
          productTour.goToNext();
        }
      })
      .catch(error => {
        console.log(error);
        enqueueSnackbar("Failed to send an email", { variant: "error" });
        handleShareClose();
      })
      .finally(() => {
        setIsSharingRoute(false);
      });
  };

  const handleWindowChange = tabIndex => {
    setCurrentWindow("Project");
    setExtendableType(null);
    setExtendableId(null);
    setProjectTabValue(tabIndex);
  };

  const routes = solution ? solution.routes : [];

  // Warn about unsaved changes if action closes open window
  const warnWindowChange = tabIndex => {
    if (!saveDisabled || !settingsSaveDisabled) {
      setChangesDialog({ visible: true, tabIndex: tabIndex });
    } else {
      handleWindowChange(tabIndex);
    }
  };

  const warnReopen = extendableType => {
    if (!saveDisabled) {
      setChangesDialog({ visible: true, action: "expandable", extendableType });
    } else {
      reopenExtendable(extendableType);
    }
  };

  const handleReoptimizeSelectedRoutes = (e, acceptedChanges = false) => {
    console.log(
      published,
      isEditMode,
      !isSelectedRoutesHasStartedRoutes,
      !confirmDialog?.changesAccepted
    );
    if (
      published &&
      isEditMode &&
      !isSelectedRoutesHasStartedRoutes &&
      !confirmDialog?.changesAccepted
    ) {
      setConfirmDialog({
        visible: true,
        type: "saveDisabled",
        action: "continue",
        actionType: "confirm_changes"
      });
    } else if (published && isEditMode && isSelectedRoutesHasStartedRoutes) {
      setConfirmDialog({
        visible: true,
        type: "saveDisabled",
        action: "reoptimize",
        actionType: "confirm_started"
      });
    } else if (unassignedStopsCount > 0) {
      setConfirmDialog({
        visible: true,
        type: "saveDisabled",
        action: "reoptimize",
        actionType: "reoptimize_unscheduled"
      });
    } else if ((!saveDisabled || !settingsSaveDisabled) && !acceptedChanges) {
      setChangesDialog({
        visible: true,
        type: "saveDisabled",
        action: "reoptimizeRoutes"
      });
    } else {
      reoptimizeRoutes();
    }
  };

  const { servicesQuantity, finishedStops, skippedStops } = countStops(
    routes,
    plan.ft_live_status_updates
  );

  // Close expandables on publish change
  useEffect(() => {
    setOptionsExpanded(false);
    setStatsExpanded(false);
    clearDriverLocations();
  }, [published, clearDriverLocations, setOptionsExpanded]);

  useEffect(() => {
    if (isProductTOurStepProjectPublishShareDialog) {
      setShareRouteEmail(userData.email);
    }
  }, [isProductTOurStepProjectPublishShareDialog, userData.email]);

  return (
    <>
      <Box
        flexDirection="column"
        height="100%"
        overflow="hidden"
        display={currentWindow === "Optimized" ? "flex" : "none"}
        id="optimized_project_wrapper"
      >
        {/* Loading 
        <Fade in={loadingRoutesList} mountOnEnter unmountOnExit>
          <LoadingCircle />
        </Fade>
*/}
        {/* Top bar
        <Box>
          {!published && (
            <Box className={classes.header} mb={1.25}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                height={72}
                pl={3}
                pr={3}
              >
                <Box display="flex" alignItems="center" height={72}>
                  <Button
                    onClick={() => warnWindowChange(0)}
                    className={clsx(classes.tabButton)}
                    variant="outlined"
                    data-cy="stopsTab-btn"
                  >
                    <PinIcon fontSize="small" color={"action"} />

                    <Box component="span" ml={1}>
                      Stops ({stopsLength})
                    </Box>
                  </Button>

                  <Button
                    onClick={() => warnWindowChange(1)}
                    className={clsx(classes.tabButton)}
                    variant="outlined"
                    data-cy="vehiclesTab-btn"
                  >
                    <TruckIcon fontSize="small" color={"action"} />

                    <Box component="span" ml={1}>
                      Vehicles ({vehiclesLength})
                    </Box>
                  </Button>
                </Box>
                <OptimizedTooltip placement="bottom" title="Project settings">
                  <IconButton
                    onClick={() => {
                      setOptionsExpanded(!optionsExpanded);
                      element.current.scrollTop = 0;
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </OptimizedTooltip>
              </Box>
            </Box>
          )}
        </Box>
        */}
        {/* Route groups */}
        <Slide in={!optionsExpanded} direction="up">
          <Box>
            {/* Header

            <Box className={classes.header} mb={1.25}>
              <Box
                height={72}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={3}
              >
                <Box width={published ? 200 : 140}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography color="secondary" className={classes.scheduled}>
                      {published
                        ? `Completed (${
                            Math.floor(
                              ((finishedStops + skippedStops) /
                                servicesQuantity) *
                                100
                            ) || 0
                          }%)`
                        : "Scheduled"}
                    </Typography>
                    <Typography
                      className={classes.scheduled}
                      data-cy="scheduledIndicator"
                    >
                      {published
                        ? `${
                            finishedStops + skippedStops
                          } / ${servicesQuantity}`
                        : `${servicesQuantity} / ${
                            servicesQuantity + unassigned.length
                          }`}
                    </Typography>
                  </Box>

                  {published ? (
                    <LinearProgress
                      variant="buffer"
                      value={Math.floor(
                        (finishedStops / servicesQuantity) * 100
                      )}
                      valueBuffer={Math.floor(
                        ((finishedStops + skippedStops) / servicesQuantity) *
                          100
                      )}
                    />
                  ) : (
                    <LinearProgress
                      variant="determinate"
                      value={Math.floor(
                        (servicesQuantity /
                          (servicesQuantity + unassigned.length)) *
                          100
                      )}
                    />
                  )}
                </Box>

                <Box display="flex" alignItems="center">
                  {!published ? (
                    <>
                      <OptimizedTooltip
                        placement="bottom"
                        title={
                          unassigned.length ? "Unscheduled stops" : "Add stops"
                        }
                      >
                        <IconButton
                          data-cy="toggleUnscheduledPanel"
                          onClick={() => {
                            warnReopen("unscheduled");
                          }}
                          className={classes.iconUnshceduled}
                        >
                          {unassigned.length ? (
                            <Badge
                              badgeContent={unassigned.length}
                              color="error"
                            >
                              <UnassignedIcon />
                            </Badge>
                          ) : (
                            <AddTasksIcon />
                          )}
                        </IconButton>
                      </OptimizedTooltip>

                      <OptimizedTooltip placement="bottom" title="Drivers">
                        <IconButton
                          onClick={() => {
                            warnReopen("drivers");
                          }}
                          data-cy="extendable-openDriversBtn"
                        >
                          <DriversIcon />
                        </IconButton>
                      </OptimizedTooltip>
                    </>
                  ) : (
                    <>
                      <Box className={classes.verticalDivider} mx={1} />

                      <OptimizedTooltip
                        placement="bottom"
                        title="Stops statistics"
                        data-cy="toggleStatistics"
                      >
                        <IconButton
                          onClick={() => {
                            setStatsExpanded(!statsExpanded);
                          }}
                        >
                          <ExpandMoreIcon
                            className={clsx(classes.expandIcon, {
                              [classes.rotate180]: statsExpanded
                            })}
                          />
                        </IconButton>
                      </OptimizedTooltip>
                    </>
                  )}
                </Box>
              </Box>
              // Options
              <Box>
                {published && (
                  <StopStats
                    expanded={statsExpanded}
                    allStops={servicesQuantity}
                    finishedStops={finishedStops}
                    skippedStops={skippedStops}
                  />
                )}
              </Box>
            </Box>
          */}
            {showingRouteId && (
              <Box className={clsx(classes.routeWrapper)}>
                <Route warnReopen={warnReopen} shareRoute={shareRoute} />
              </Box>
            )}
            {!showingRouteId && (
              <Box
                id="routes-list"
                className={clsx(classes.routesList, {
                  [classes.routesListSm]: !published
                })}
              >
                <RouteGroups
                  warnReopen={warnReopen}
                  handleReoptimizeSelectedRoutes={
                    handleReoptimizeSelectedRoutes
                  }
                  setShareRouteEmail={setShareRouteEmail}
                  shareRoute={shareRoute}
                />
              </Box>
            )}
          </Box>
        </Slide>
      </Box>
      <AddDriverDialog />
      <FormDialog
        isShareRoute
        title="Share with your driver"
        label="Send link to"
        dataCy="shareRoute-driverInput"
        placeholder="Driver’s email address"
        confirmText="Send"
        routeIds={shareRouteIds}
        value={shareRouteEmail}
        setValue={setShareRouteEmail}
        open={showShare}
        action={handleSend}
        handleClose={handleShareClose}
        id="share_with_driver_dialog"
        cancelDisabled={isProductTOurStepProjectPublishShareDialog}
        confirmId="share_with_driver_send_button"
        disableEscapeKeyDown={isProductTOurStepProjectPublishShareDialog}
        errorText={shareRouteEmailError}
      />
      <UnsavedChangesDialog
        open={changesDialog.visible}
        action={e => {
          switch (changesDialog.action) {
            case "expandable":
              changesDialog.extendableType
                ? reopenExtendable(changesDialog.extendableType)
                : handleWindowChange(changesDialog.tabIndex);
              break;
            case "reoptimizeRoutes":
              handleReoptimizeSelectedRoutes(e, true);
              break;
            // No default
          }
        }}
        handleClose={() => setChangesDialog({ visible: false })}
      />
      {/* {
  "routes_id" : [String, String, String], //required
  "optimize_started_routes" : Boolean, (default true) //not required
  "optimize_unassigned_stops" : Boolean (default true) //not required
} */}
      {confirmDialog && (
        <ConfirmDialog
          open={confirmDialog.visible}
          handleClose={() =>
            setConfirmDialog({
              ...confirmDialog,
              visible: false
            })
          }
          action={(e, dialogState) => {
            if (confirmDialog?.actionType === "reoptimize_unscheduled") {
              if (!isSelectedRoutesHasStartedRoutes) {
                dialogState.checked
                  ? reoptimizeRoutes(selectedRoutes)
                  : reoptimizeRoutes(selectedRoutes, {
                      optimize_unassigned_stops: false
                    });
              } else {
                dialogState.selectedValue ===
                "reoptimize_routes_that_have_not_started"
                  ? dialogState.checked
                    ? reoptimizeRoutes(selectedRoutes) //Default optimize (optimize default settings you can see inside reoptimizeRoutes function)
                    : reoptimizeRoutes(selectedRoutes, {
                        optimize_started_routes: false,
                        optimize_unassigned_stops: false
                      })
                  : dialogState.checked
                  ? reoptimizeRoutes(selectedRoutes, {
                      optimize_started_routes: true
                    })
                  : reoptimizeRoutes(selectedRoutes, {
                      optimize_unassigned_stops: false,
                      optimize_started_routes: true
                    });
              }
            }
            if (confirmDialog?.actionType === "confirm_changes") {
              if (unassignedStopsCount > 0) {
                Promise.resolve().then(res => {
                  setConfirmDialog({
                    visible: true,
                    type: "saveDisabled",
                    action: "reoptimize",
                    actionType: "reoptimize_unscheduled"
                  });
                });
              } else {
                reoptimizeRoutes(selectedRoutes);
              }
            }
            if (confirmDialog?.actionType === "confirm_started") {
              if (unassignedStopsCount > 0) {
                Promise.resolve().then(res => {
                  setConfirmDialog({
                    visible: true,
                    type: "saveDisabled",
                    action: "reoptimize",
                    actionType: "reoptimize_unscheduled"
                  });
                });
              } else {
                if (
                  dialogState?.selectedValue ===
                  "reoptimize_routes_that_have_not_started"
                ) {
                  reoptimizeRoutes(selectedRoutes);
                } else {
                  reoptimizeRoutes({
                    optimize_started_routes: true
                    // optimize_unassigned_stops: true
                  });
                }
              }
            }
          }}
          title={
            confirmDialog?.actionType?.includes("confirm")
              ? "Are you sure you want to reoptimize your routes?"
              : "You have unscheduled stops in your project"
          }
          body={
            <>
              <Typography variant="p">
                {/* If our modal is confirm type just we need to show information message about changes if it is not we need to inform about action that will happen when user clicks the confirm button */}
                {confirmDialog?.actionType?.includes("confirm")
                  ? "Routes that are locked will not be affected, but all other routes may change. This action cannot be reversed, your drivers' routes will update immediately."
                  : `Do you want to try to plan ${unassignedStopsCount} unscheduled stop${
                      unassignedStopsCount > 1 ? "s" : ""
                    } in your routes?`}
              </Typography>
            </>
          }
          confirmText={
            confirmDialog?.actionType?.includes("confirm")
              ? unassignedStopsCount > 0
                ? "Continue"
                : "Reoptimize"
              : capitalize(confirmDialog?.action)
          }
          modalType={
            confirmDialog?.actionType === "confirm_changes"
              ? "withText"
              : confirmDialog?.actionType === "confirm_started"
              ? "withRadioGroup"
              : confirmDialog?.actionType === "reoptimize_unscheduled"
              ? "withCheckbox"
              : ""
          }
        />
      )}
    </>
  );
}

function mapStateToProps(state) {
  return {
    userData: state.auth.userData,
    published:
      ["published", "expired", "finished"].includes(
        state.project.status.toLowerCase()
      ) || state.project.published_once,
    loadingRoutesList: state.loading.routesList,
    stopsLength: state.project.vrp.stops.filter(
      task =>
        task.type !== "start" &&
        task.type !== "end" &&
        !isEmptyObj(task) &&
        task.type !== "break"
    ).length,
    vehiclesLength: state.project.selectedVehicles.length,
    solution: state.project.solution,
    saveDisabled: state.extendable.saveDisabled,
    settingsSaveDisabled: state.common.settingsSaveDisabled,
    plan:
      (state.auth.userData.organisationSite &&
        state.auth.userData.organisationSite.organisation.plan) ||
      {},
    optionsExpanded: state.common.optionsExpanded,
    showingRouteId: state.project.showingRouteId,
    projectId: state.project.id,
    unassignedStopsCount: state.project.solution.unassigned.stops.length,
    isEditMode: state.project.status.toLowerCase() === "optimized",
    isSelectedRoutesHasStartedRoutes: state.project.solution.routes.some(
      route =>
        state.common.selectedRoutes.includes(route.id) &&
        route.stops.some(stop => stop.status === "finished")
    ),
    selectedRoutes: state.common.selectedRoutes,
    projectTabValue: state.common.projectTabValue
  };
}

export default connect(mapStateToProps, {
  setExtendableType,
  reopenExtendable,
  setProjectTabValue,
  setExtendableId,
  setCurrentWindow,
  clearDriverLocations,
  setOptionsExpanded,
  reoptimizeRoutes,
  setSelectedRoutes,
  setDragSelection
})(withSnackbar(Optimized));
